import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Table from '@/components/tables/Table';
import { Activity } from '../types/activityTypes';
import { Link } from 'react-router-dom';
import Badge from '@/components/ui/badge/Badge';
import { useTableColumns } from '@/shared/hooks/useTableColumns';
import { Modal } from '@/components/ui/modal';
import Button from '@/components/ui/button/Button';
import { useDeleteActivity } from '../hooks/useActivities';
import { useToast } from '@/components/ui/toast/Toast';
import ActivityForm from './ActivityForm';
import { UpdateActivityDto } from '../api/activitiesApi';
import CarReportForm from '@/features/cars/components/CarReportForm';
import { useQueryClient } from '@tanstack/react-query';
import EditCarReportForm from '@/features/cars/components/EditCarReportForm';
import { carReportsApi } from '@/features/cars/api/carReportsApi';
import { useCars } from '@/features/cars/hooks/useCars';

interface ActivitiesTableProps {
  activities: Activity[];
  isLoading: boolean;
  onSort?: (orderBy: string) => void;
  currentOrder?: string;
  onEditActivity: (data: UpdateActivityDto) => Promise<void>;
}

const formatDeliveryDate = (date: string) => {
  return new Date(date).toLocaleDateString();
};

const formatTime = (string: string) => {
  return string.slice(0, 5);
};

const getGoogleMapsUrl = (latitude: string, longitude: string) => {
  return `https://maps.google.com/?q=${latitude},${longitude}`;
};

export const ActivitiesTable: React.FC<ActivitiesTableProps> = ({
  activities,
  isLoading,
  onSort,
  currentOrder,
  onEditActivity,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { visibleColumns } = useTableColumns();
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isEditReportModalOpen, setIsEditReportModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const deleteActivity = useDeleteActivity();
  const formRef = React.useRef<{ handleClose: () => void; isDirty: () => boolean } | null>(null);
  const queryClient = useQueryClient();
  const { data: carsResponse } = useCars({
    limit: 500,
  });
  const cars = carsResponse || [];

  const getEventTypeBadgeProps = (type: string) => {
    switch (type.toLowerCase()) {
      case 'delivery_to_client':
        return { color: 'success' as const, variant: 'light' as const, text: t('activities.filters.event_type.delivery') };
      case 'pickup_from_client':
        return { color: 'error' as const, variant: 'light' as const, text: t('activities.filters.event_type.pickup') };
      case 'handoff_to_service':
        return { color: 'warning' as const, variant: 'light' as const, text: t('activities.filters.event_type.to_service') };
      case 'receiving_from_service':
        return { color: 'info' as const, variant: 'light' as const, text: t('activities.filters.event_type.from_service') };
      default:
        return { color: 'light' as const, variant: 'light' as const, text: type };
    }
  };

  const handleSort = (field: string) => {
    if (!onSort) return;
    const newOrder = currentOrder === field ? `-${field}` : field;
    onSort(newOrder);
  };

  const handleCancelClick = (activity: Activity) => {
    setSelectedActivity(activity);
    setIsCancelModalOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!selectedActivity) return;

    try {
      await deleteActivity.mutateAsync(selectedActivity.id);
      setIsCancelModalOpen(false);
      setSelectedActivity(null);
      toast.success(t('activities.delete.success'));
    } catch (error) {
      toast.error(t('activities.delete.error'));
    }
  };

  const closeCancelModal = () => {
    setIsCancelModalOpen(false);
    setSelectedActivity(null);
  };

  const handleEditClick = (activity: Activity) => {
    setSelectedActivity(activity);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedActivity(null);
  };

  const handleCreateReport = (activity: Activity) => {
    setSelectedActivity(activity);
    setIsReportModalOpen(true);
  };

  const handleEditReport = async (activity: Activity) => {
    if (!activity.report) return;

    setSelectedActivity(activity);
    try {
      const report = await carReportsApi.getReport(activity.report);
      setSelectedReport(report);
      setIsEditReportModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch report:', error);
    }
  };

  const closeReportModal = () => {
    setIsReportModalOpen(false);
    setSelectedActivity(null);
  };

  const closeEditReportModal = () => {
    setIsEditReportModalOpen(false);
    setSelectedActivity(null);
    setSelectedReport(null);
  };

  const columns = useMemo(() => [
    {
      key: 'type',
      header: t('activities.columns.type'),
      field: 'type',
      cell: (row: Activity) => {
        const badgeProps = getEventTypeBadgeProps(row.type);
        return (
          <>
            <Badge color={badgeProps.color} variant={badgeProps.variant} size="md">
              {badgeProps.text}
            </Badge>

            {row.renting && (
              <Link to={`/rentings/${row.renting.id}`} className="flex">
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                >
                  {t('activities.table.view_renting')}
                </Button>
              </Link>
            )}
          </>
        );
      },
      sortable: true,
      onSort: () => handleSort('type')
    },
    {
      key: 'car',
      header: t('activities.columns.car'),
      field: 'car',
      cell: (row: Activity) => (
        <Link
          to={row.car ? `/cars/${row.car.id}` : '#'}
          className="font-medium text-text-primary hover:text-brand-500 transition-colors"
          title={t('activities.table.view_details')}
        >
          {`${row.car.model.brand_name} ${row.car.model.model_name} (${row.car.registration_number})`}
        </Link>
      ),
    },
    {
      key: 'datetime',
      header: t('activities.columns.datetime'),
      field: 'datetime',
      cell: (row: Activity) => (
        <div className="flex flex-col">
          <span className="text-lg flex items-baseline gap-1 text-text-primary">
            {formatDeliveryDate(row.activity_date)}
            <span className="text-text-tertiary text-sm text-normal">
              {formatTime(row.activity_time)}
            </span>
          </span>
        </div>
      ),
      sortable: true,
      onSort: () => handleSort('activity_date')
    },
    {
      key: 'address',
      header: t('activities.columns.address'),
      field: 'address',
      cell: (row: Activity) => {
        const latitude = row.latitude || '';
        const longitude = row.longitude || '';

        return (
          <a
            href={getGoogleMapsUrl(latitude, longitude)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-brand-500 truncate hover:text-brand-600 hover:underline w-fit max-w-[200px]"
            title={t('activities.table.open_maps')}
          >
            {row.address || (row.renting ? row.renting.address_to_deliver : '')}
          </a>
        );
      },
    },
    {
      key: 'comment',
      header: t('activities.columns.comment'),
      field: 'comment',
      cell: (row: Activity) => (
        <div className="max-w-[200px]">
          {row.comment ? (
            <span className="text-sm text-text-secondary line-clamp-2" title={row.comment}>
              {row.comment}
            </span>
          ) : (
            <span className="text-sm text-text-tertiary">{t('activities.table.no_comment')}</span>
          )}
        </div>
      ),
    },
    {
      key: 'report',
      header: t('activities.columns.report'),
      field: 'report',
      cell: (activity: Activity) => (
        <div>
          {activity.report ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleEditReport(activity)}
              className="!bg-primary/10 hover:!bg-primary/20 !text-primary"
            >
              {t('activities.table.view_report')}
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCreateReport(activity)}
            >
              {t('activities.table.add_report')}
            </Button>
          )}
        </div>
      ),
      sortable: false
    },
    {
      key: 'actions',
      header: t('activities.columns.actions'),
      field: 'actions',
      cell: (row: Activity) => (
        <div className="flex gap-2 justify-end">
          <Button
            testId="activity-edit-button"
            variant="outline"
            size="sm"
            onClick={() => handleEditClick(row)}
          >
            {t('Edit')}
          </Button>
          <Button
            testId="activity-delete-button"
            variant="danger"
            size="sm"
            onClick={() => handleCancelClick(row)}
          >
            {t('Delete')}
          </Button>
        </div>
      ),
    },
  ], [t, currentOrder, onSort]);

  const filteredColumns = useMemo(() => {
    if (visibleColumns && visibleColumns.length > 0) {
      return columns.filter(col => visibleColumns.includes(col.key));
    }
    return columns;
  }, [columns, visibleColumns]);

  return (
    <>
      <Table
        columns={filteredColumns}
        data={activities}
        loading={isLoading}
      />

      <Modal
        isOpen={isCancelModalOpen}
        onClose={closeCancelModal}
        className="max-w-[400px] p-6"
      >
        <div className="text-center">
          <h3 className="mb-4 text-xl font-semibold text-text-primary">
            {t('activities.cancel.title')}
          </h3>
          <p className="mb-6 text-text-tertiary">
            {t('activities.cancel.message')}
          </p>
          <div className="flex justify-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={closeCancelModal}
            >
              {t('activities.cancel.keep')}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleConfirmCancel}
              disabled={deleteActivity.isPending}
            >
              {t('activities.cancel.confirm')}
            </Button>
          </div>
        </div>
      </Modal>

      {selectedActivity && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          maxWidth="40rem"
        >
          <ActivityForm
            initialData={{
              type: selectedActivity.type,
              car_id: selectedActivity.car.id,
              activity_date: selectedActivity.activity_date,
              activity_time: selectedActivity.activity_time,
              address: selectedActivity.address,
              comment: selectedActivity.comment || '',
              deliver_latitude: selectedActivity.latitude || '',
              deliver_longitude: selectedActivity.longitude || '',
              id: selectedActivity.id,
            }}
            onSubmit={onEditActivity}
            onClose={handleCloseEditModal}
          />
        </Modal>
      )}

      <Modal
        isOpen={isReportModalOpen}
        onClose={closeReportModal}
        maxWidth="1300px"
        className="p-6 lg:p-10"
      >
        {selectedActivity && (
          <CarReportForm
            carId={selectedActivity.car.id}
            activityId={selectedActivity.id}
            initialReportType={selectedActivity.type}
            onSubmit={() => {
              queryClient.invalidateQueries({ queryKey: ['activities'] });
              setIsReportModalOpen(false);
              setSelectedActivity(null);
            }}
            onClose={closeReportModal}
          />
        )}
      </Modal>

      <Modal
        isOpen={isEditReportModalOpen}
        onClose={closeEditReportModal}
        maxWidth="1300px"
        className="p-6 lg:p-10"
      >
        {selectedReport && (
          <EditCarReportForm
            report={selectedReport}
            onSubmit={() => {
              queryClient.invalidateQueries({ queryKey: ['activities'] });
              setIsEditReportModalOpen(false);
              setSelectedActivity(null);
              setSelectedReport(null);
            }}
            onClose={closeEditReportModal}
          />
        )}
      </Modal>
    </>
  );
};
